import { Box, Stack, Typography } from "@mui/material";
import approversAndSignersStyles from "../ApproversAndSigners.styles";
import recipientsTokens from "../ApproversAndSigners.tokens";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import { isTruthy } from "helpers/methods";
import { Reviewers } from "models/interfaces";
import React, { useRef } from "react";
import { setContacts, updateIndividualReviewer } from "redux/appSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { getContactSuggestion } from "../SignRequestHelper";
import strings from "global/constants/StringConstants";
import {
  getParticipantNameError,
  isValidParticipantNameLength,
} from "screens/Shared/CommonHelper";

const styles = approversAndSignersStyles;
const { row } = recipientsTokens;

const ApprovalFormFields = (props: {
  eachApprovers: Reviewers;
  contactList: any;
  setContactList: Function;
  handleRemoveContacts: Function;
  handleResetContacts: Function;
  index?: number;
  excludeIds: string[];
  setExcludeIds: Function;
  handleDelete: any;
  isMobile?: any;
}) => {
  const dispatch = useAppDispatch();
  let { errors, contacts } = useAppSelector((state: RootState) => state.app);

  const timeoutRef: any = useRef(null);
  const timeoutRef2: any = useRef(null);

  const getTextFields = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: row.gap,
          width: "100%",
          py: props.isMobile ? 1 : 0,
        }}
      >
        <Box sx={styles.nameField}>
          <CustomAutoComplete
            id={`signer-${props.index}-name`}
            ariaLabel={`Approver ${(props.index ?? 0) + 1} name`}
            placeHolder="Approver name"
            name="name"
            freeSolo={true}
            value={props.eachApprovers.name || ""}
            onChange={(e: any, newValue: any, reason: any) => {
              if (newValue && typeof newValue === "object") {
                props.setExcludeIds([
                  ...props.excludeIds.filter(
                    (id) => id !== props.eachApprovers.email,
                  ),
                  newValue.email,
                ]);

                dispatch(
                  updateIndividualReviewer({
                    reviewerId: props.eachApprovers.id,
                    fieldsToUpdate: {
                      name: newValue.name.substring(0, 200) || "",
                      email:
                        newValue.email.substring(0, 256) ||
                        props.eachApprovers.email.substring(0, 256),
                    },
                  }),
                );
              } else if (typeof newValue === "string") {
                dispatch(
                  updateIndividualReviewer({
                    reviewerId: props.eachApprovers.id,
                    fieldsToUpdate: {
                      name: newValue
                        .trimStart()
                        .replace(/(?:^\s+|\s+$|\t+)/g, " ")
                        .substring(0, 200),
                    },
                  }),
                );
              } else if (newValue === null || newValue === "") {
                dispatch(
                  updateIndividualReviewer({
                    reviewerId: props.eachApprovers.id,
                    fieldsToUpdate: {
                      name: "",
                    },
                  }),
                );
              }

              props.handleRemoveContacts();
            }}
            onBlur={props.handleResetContacts}
            onChangeForTextField={(
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              const { value } = e.target;
              dispatch(
                updateIndividualReviewer({
                  reviewerId: props.eachApprovers.id,
                  fieldsToUpdate: {
                    name: value.trimStart().replace(/(?:^\s+|\s+$|\t+)/g, " "),
                  },
                }),
              );

              clearTimeout(timeoutRef.current);

              timeoutRef.current = setTimeout(async () => {
                if (isTruthy(value.trim())) {
                  dispatch(setContacts([]));
                  await getContactSuggestion(
                    value.trim(),
                    dispatch,
                    props.excludeIds,
                    props.setContactList,
                    props.index,
                    "name",
                  );
                } else {
                  await getContactSuggestion(
                    "",
                    dispatch,
                    props.excludeIds,
                    props.setContactList,
                    props.index,
                    "name",
                  );
                }
              }, 500);
            }}
            filterOptions={(options: any, { inputValue }: any) =>
              options.filter(
                (option: any) =>
                  option.name
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()) ||
                  option.email.toLowerCase().includes(inputValue.toLowerCase()),
              )
            }
            renderOption={(prop: any, option: any) => (
              <li {...prop}>
                <Stack direction="column">
                  <Typography variant="subtitle1" fontWeight={"bold"}>
                    {option.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "grey" }}>
                    {option.email}
                  </Typography>
                </Stack>
              </li>
            )}
            ref={timeoutRef}
            options={(props.contactList.name &&
            props.contactList.name[props.index!]
              ? props.contactList.name[props.index!]
              : contacts || []
            )?.filter(
              (c: any) =>
                !props.excludeIds
                  .map((id: string) => id.toLowerCase())
                  .includes(c.email.toLowerCase()),
            )}
            getOptionLabel={(option: any) => {
              if (typeof option === "string") return option;
              return option.name || "";
            }}
            error={
              !(
                isTruthy(props.eachApprovers.name) &&
                isValidParticipantNameLength(props.eachApprovers.name)
              ) && errors.name
            }
            errorMessage={
              !isTruthy(props.eachApprovers.name) && errors.name
                ? "Name is required"
                : getParticipantNameError(props.eachApprovers.name) || ""
            }
            PaperComponent={(paperProps: any) => (
              <Stack
                {...paperProps}
                width={"100%"}
                mt={2}
                mb={2}
                bgcolor={"white"}
                boxShadow="0px 4px 6px -1px rgb(0 0 0 / 0.1)"
                sx={{
                  ...(props.isMobile && {
                    "& .MuiAutocomplete-listbox": {
                      overflowX: "auto",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                    },
                    "& .MuiAutocomplete-option": {
                      whiteSpace: "nowrap",
                      minWidth: "max-content",
                    },
                  }),
                }}
              />
            )}
          />
        </Box>
        <Box sx={styles.emailField}>
          <CustomAutoComplete
            id={`signer-${props.index}-email`}
            ariaLabel={`Approver ${(props.index ?? 0) + 1} email`}
            placeHolder="Approver email"
            name="email"
            freeSolo={true}
            value={props.eachApprovers.email || ""}
            onChange={(e: any, newValue: any, reason: any) => {
              if (newValue && typeof newValue === "object") {
                props.setExcludeIds([
                  ...props.excludeIds.filter(
                    (id) => id !== props.eachApprovers.email,
                  ),
                  newValue.email,
                ]);

                dispatch(
                  updateIndividualReviewer({
                    reviewerId: props.eachApprovers.id,
                    fieldsToUpdate: {
                      name:
                        newValue.name.substring(0, 200) ||
                        props.eachApprovers.name.substring(0, 200),
                      email: newValue.email.substring(0, 256) || "",
                    },
                  }),
                );
              } else if (typeof newValue === "string") {
                dispatch(
                  updateIndividualReviewer({
                    reviewerId: props.eachApprovers.id,
                    fieldsToUpdate: {
                      email: newValue.replace(/\s/g, "").substring(0, 256),
                    },
                  }),
                );
              } else if (newValue === null || newValue === "") {
                // Cleared field
                dispatch(
                  updateIndividualReviewer({
                    reviewerId: props.eachApprovers.id,
                    fieldsToUpdate: {
                      email: "",
                    },
                  }),
                );
              }

              props.handleRemoveContacts();
            }}
            onBlur={props.handleResetContacts}
            onChangeForTextField={(
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              const { value } = e.target;
              dispatch(
                updateIndividualReviewer({
                  reviewerId: props.eachApprovers.id,
                  fieldsToUpdate: {
                    email: value.replace(/\s/g, "").substring(0, 256),
                  },
                }),
              );

              clearTimeout(timeoutRef2.current);

              timeoutRef2.current = setTimeout(() => {
                if (isTruthy(value.trim())) {
                  getContactSuggestion(
                    value.trim(),
                    dispatch,
                    props.excludeIds,
                    props.setContactList,
                    props.index,
                    "email",
                  );
                } else {
                  dispatch(setContacts([]));
                }
              }, 500);
            }}
            filterOptions={(options: any, { inputValue }: any) =>
              options.filter(
                (option: any) =>
                  option.email
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()) ||
                  option.name.toLowerCase().includes(inputValue.toLowerCase()),
              )
            }
            renderOption={(prop: any, option: any) => (
              <li {...prop}>
                <Stack direction="column">
                  <Typography variant="subtitle1" fontWeight={"bold"}>
                    {option.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "grey" }}>
                    {option.email}
                  </Typography>
                </Stack>
              </li>
            )}
            ref={timeoutRef2}
            options={(props.contactList.email &&
            props.contactList.email[props.index!]
              ? props.contactList.email[props.index!]
              : contacts || []
            )?.filter(
              (c: any) =>
                !props.excludeIds
                  .map((id: string) => id.toLowerCase())
                  .includes(c.email.toLowerCase()),
            )}
            getOptionLabel={(option: any) => {
              if (typeof option === "string") return option;
              return option.email || "";
            }}
            error={
              (!isTruthy(props.eachApprovers.email) ||
                !strings.regex.test(props.eachApprovers.email)) &&
              errors.email
            }
            PaperComponent={(paperProps: any) => (
              <Stack
                {...paperProps}
                width={"100%"}
                mt={2}
                mb={2}
                bgcolor={"white"}
                boxShadow="0px 4px 6px -1px rgb(0 0 0 / 0.1)"
                sx={{
                  ...(props.isMobile && {
                    "& .MuiAutocomplete-listbox": {
                      overflowX: "auto",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                    },
                    "& .MuiAutocomplete-option": {
                      whiteSpace: "nowrap",
                      minWidth: "max-content",
                    },
                  }),
                }}
              />
            )}
          />
        </Box>
      </Box>
    );
  };

  return getTextFields();
};

export default ApprovalFormFields;
